import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, ExternalLink, AlertTriangle } from 'lucide-react';
import { vatReportingApi } from '../../services/vatReportingApi';
import { getBcPostedDocumentUrl, bcLinkHint } from '../../utils/bcLinks';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { ColumnFilter } from '../ui/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import { useVatLang } from './VatLang';
import { EntryExclusionDialog, type ExclusionTarget } from './EntryExclusionDialog';
import { AmountOverrideDialog, ReportingAmountCell } from './AmountOverrideDialog';
import {
    ExclusionActionCell, exclusionRowClass, exclusionTargetFor, useExclusionTitle,
} from './exclusionRow';
import type {
    AmountOverrideField, ExclusionAction, VatCompany, VatEntryRow,
} from '../../types/vatReporting';

/**
 * The transactions behind a figure, and the route from each one to its document.
 *
 * TWO LINKS, TWO MEANINGS, AND THE DIFFERENCE IS SHOWN.
 *   DocuWare  the scanned original. Incoming documents only - a sales invoice was produced by
 *             BC and never scanned, so there is nothing to open and the cell says so instead
 *             of offering a link that fails. Measured 2026-01..07: dch purchases 48/50 have a
 *             scan, sales 0/66 - by design, not a gap.
 *   BC        the posted document card. dch only; dbi runs redPoint, which has no web client.
 *             Sales documents resolve too since the sales-header probe was added: 66/66 dch
 *             sales entries now reach a card, where they previously offered nothing at all.
 *
 * The table follows the project standard - sticky header, chevron sort, drag-resizable
 * columns - so a few hundred rows can actually be worked through.
 */

interface Props {
    isOpen: boolean;
    onClose: () => void;
    company: VatCompany;
    year: number;
    month: number;
    title: string;
    subtitle?: string;
    filter: {
        kennzahl?: string;
        vatBusPostingGroup?: string;
        vatProdPostingGroup?: string;
        direction?: string;
        entryNos?: number[];
        includeSettlement?: boolean;
        /** Show manually excluded postings, struck through. Set by a control's evidence only. */
        includeExcluded?: boolean;
    };
    canEdit?: boolean;
    /** Called after a manual decision, so the caller reloads the figures it is showing. */
    onChanged?: () => void;
}

const money = (v: number | null, dp = 2) => v === null || v === undefined
    ? '—'
    : new Intl.NumberFormat('de-CH', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(v);

type ColKey =
    | 'entryNo' | 'reportingDate' | 'documentNo' | 'documentType' | 'counterparty'
    | 'counterpartyVatId' | 'groups' | 'baseLocal' | 'taxLocal' | 'baseReporting' | 'taxReporting';

const COLUMNS: { key: ColKey; labelKey: string; width: number; align?: 'right' }[] = [
    { key: 'entryNo', labelKey: 'drill.entry', width: 80 },
    { key: 'reportingDate', labelKey: 'drill.date', width: 100 },
    { key: 'documentNo', labelKey: 'drill.document', width: 140 },
    { key: 'documentType', labelKey: 'drill.type', width: 110 },
    { key: 'counterparty', labelKey: 'drill.counterparty', width: 220 },
    { key: 'counterpartyVatId', labelKey: 'drill.partnerVatId', width: 140 },
    { key: 'groups', labelKey: 'drill.groups', width: 200 },
    { key: 'baseLocal', labelKey: 'drill.baseLocal', width: 120, align: 'right' },
    { key: 'taxLocal', labelKey: 'drill.taxLocal', width: 110, align: 'right' },
    { key: 'baseReporting', labelKey: 'drill.baseReporting', width: 120, align: 'right' },
    { key: 'taxReporting', labelKey: 'drill.taxReporting', width: 110, align: 'right' },
];

const ACCESSOR: Record<ColKey, (r: VatEntryRow) => string | number | null> = {
    entryNo: r => r.entryNo,
    reportingDate: r => r.reportingDate,
    documentNo: r => r.documentNo,
    documentType: r => r.documentType,
    counterparty: r => r.counterpartyName ?? r.counterpartyNo,
    counterpartyVatId: r => r.counterpartyVatId,
    groups: r => `${r.vatBusPostingGroup} / ${r.vatProdPostingGroup}`,
    baseLocal: r => r.baseLocal,
    taxLocal: r => r.taxLocal,
    baseReporting: r => r.baseReporting,
    taxReporting: r => r.taxReporting,
};

const NUMERIC: ColKey[] = ['entryNo', 'baseLocal', 'taxLocal', 'baseReporting', 'taxReporting'];

const FILTER_COLUMNS = COLUMNS.map(c => ({
    key: c.key,
    accessor: ACCESSOR[c.key],
    numeric: NUMERIC.includes(c.key),
}));

export function EntryDrillModal({
    isOpen, onClose, company, year, month, title, subtitle, filter, canEdit, onChanged,
}: Props) {
    const { t } = useVatLang();
    const [rows, setRows] = useState<VatEntryRow[]>([]);
    const [total, setTotal] = useState(0);
    const [truncated, setTruncated] = useState(false);
    const [periodLocked, setPeriodLocked] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [dialog, setDialog] = useState<{ action: ExclusionAction; target: ExclusionTarget } | null>(null);
    const [amountDialog, setAmountDialog] = useState<{ row: VatEntryRow; field: AmountOverrideField } | null>(null);
    const [sort, setSort] = useState<SortState<ColKey>>({ key: 'entryNo', direction: 'asc' });

    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLUMNS, 'vat-drill-col-widths');
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(rows, FILTER_COLUMNS);
    const filterLabels = {
        search: t('filter.search'), all: t('filter.all'), none: t('filter.none'),
        from: t('filter.from'), to: t('filter.to'), empty: t('filter.empty'),
    };

    const exclusionTitle = useExclusionTitle();

    const openAction = (action: 'exclude' | 'reset' | 'include' | 'drop', row: VatEntryRow) =>
        setDialog({ action, target: exclusionTargetFor(row) });

    // A manual EUR correction (migration 229): edit right, open period, not the settlement
    // mirror. A corrected cell still opens for everyone else, read-only, to show its history.
    const amountEditable = (r: VatEntryRow) => !!canEdit && !periodLocked && r.kind !== 'Ausgleich';
    const openAmount = (row: VatEntryRow, field: AmountOverrideField) => setAmountDialog({ row, field });

    // A drag ends with a mouseup inside the header cell, which the browser reports as a click.
    // Without this guard every resize would also re-sort the table.
    const onSort = (key: ColKey) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, NUMERIC.includes(key)));
    };

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        vatReportingApi.getEntries({ company, year, month, ...filter })
            .then(r => {
                if (cancelled) return;
                setRows(r.entries);
                setTotal(r.total);
                setTruncated(r.truncated);
                setPeriodLocked(r.periodLocked);
            })
            .catch(e => !cancelled && setError(e instanceof Error ? e.message : t('drill.loadFailed')))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
        // The filter is rebuilt by the caller on every render; serialising it keeps this from
        // re-fetching on every parent render while still reacting to a real filter change.
    }, [isOpen, company, year, month, JSON.stringify(filter), reloadKey]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        if (isOpen) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    // Sorting and totals happen over the FILTERED rows, so the footer sums never describe
    // something other than what is on screen.
    const sorted = sort.key
        ? [...filtered].sort((a, b) => {
            const acc = ACCESSOR[sort.key as ColKey];
            const av = acc(a);
            const bv = acc(b);
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const base = typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * (sort.direction === 'asc' ? 1 : -1);
        })
        : filtered;

    const sumBaseReporting = filtered.reduce((s, r) => s + (r.baseReporting ?? 0), 0);
    const sumTaxReporting = filtered.reduce((s, r) => s + (r.taxReporting ?? 0), 0);
    const anyReporting = filtered.some(r => r.baseReporting !== null);

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] max-h-[90vh] flex flex-col"
                 onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between px-5 py-3 border-b">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
                        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {activeCount > 0 && (
                    <div className="flex items-center justify-between px-5 py-1.5 border-b bg-amber-50 text-xs text-amber-900">
                        <span>{t('filter.active', { count: activeCount })}</span>
                        <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">
                            {t('filter.clearAll')}
                        </button>
                    </div>
                )}

                {/* The sticky header needs a bounded scroll container or it does not stick. */}
                <div className="flex-1 overflow-auto">
                    {loading && <div className="p-8 text-center text-gray-500">{t('drill.loading')}</div>}
                    {error && <div className="p-6 text-red-600 text-sm">{error}</div>}
                    {!loading && !error && rows.length === 0 && (
                        <div className="p-8 text-center text-gray-500">{t('drill.empty')}</div>
                    )}
                    {!loading && !error && rows.length > 0 && filtered.length === 0 && (
                        <div className="p-8 text-center text-gray-500">{t('filter.active', { count: activeCount })}</div>
                    )}
                    {!loading && !error && rows.length > 0 && filtered.length > 0 && (
                        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                            <colgroup>
                                {COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                                <col style={{ width: 130 }} />
                                <col style={{ width: 110 }} />
                            </colgroup>
                            <thead>
                                <tr className="border-b">
                                    {COLUMNS.map(c => (
                                        <SortableHeader
                                            key={c.key}
                                            label={t(c.labelKey)}
                                            sortKey={c.key}
                                            sort={sort}
                                            onSort={onSort}
                                            align={c.align}
                                            onResizeStart={x => startResize(c.key, x)}
                                            onResetWidth={() => resetWidth(c.key)}
                                            isResizing={resizingKey === c.key}
                                            filter={
                                                <ColumnFilter
                                                    label={t(c.labelKey)}
                                                    values={uniqueValuesFor(c.key)}
                                                    state={filters[c.key]}
                                                    onChange={s => setColumnFilter(c.key, s)}
                                                    numeric={NUMERIC.includes(c.key)}
                                                    active={isFiltered(c.key)}
                                                    labels={filterLabels}
                                                />
                                            }
                                        />
                                    ))}
                                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left text-xs font-medium text-gray-500">
                                        {t('drill.document')}
                                    </th>
                                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left text-xs font-medium text-gray-500">
                                        {t('excl.column')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sorted.map(r => (
                                    <tr key={r.entryNo}
                                        title={exclusionTitle(r)}
                                        className={`${r.kind === 'Ausgleich' ? 'bg-amber-50' : 'hover:bg-gray-50'} `
                                            + exclusionRowClass(r, year, month)}>
                                        <td className="px-3 py-1.5 text-gray-500 tabular-nums">{r.entryNo}</td>
                                        <td className="px-3 py-1.5 whitespace-nowrap">{r.reportingDate}</td>
                                        <td className="px-3 py-1.5 font-medium truncate" title={r.documentNo}>
                                            {r.documentNo}
                                        </td>
                                        <td className="px-3 py-1.5 text-gray-600 truncate">
                                            {r.documentType || '—'}
                                            {r.kind === 'Ausgleich' && (
                                                <span className="ml-1 text-xs text-amber-700">{t('drill.settlement')}</span>
                                            )}
                                        </td>
                                        {/* The name is what a person recognises; the number is the key. */}
                                        <td className="px-3 py-1.5 truncate"
                                            title={[r.counterpartyName, r.counterpartyNo].filter(Boolean).join(' · ')}>
                                            {r.counterpartyName ?? <span className="text-gray-400">—</span>}
                                            {r.counterpartyNo && (
                                                <span className="ml-1 text-xs text-gray-400">{r.counterpartyNo}</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-1.5 text-gray-600 truncate">{r.counterpartyVatId || '—'}</td>
                                        <td className="px-3 py-1.5 text-gray-600 truncate"
                                            title={`${r.vatBusPostingGroup} / ${r.vatProdPostingGroup}`}>
                                            {r.vatBusPostingGroup} / {r.vatProdPostingGroup}
                                            {r.disposition !== 'mapped' && (
                                                <span className="ml-1 inline-flex items-center gap-0.5 text-xs text-red-600">
                                                    <AlertTriangle className="h-3 w-3" />{r.disposition}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{money(r.baseLocal)}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{money(r.taxLocal)}</td>
                                        <td className="px-3 py-1.5 text-right">
                                            <ReportingAmountCell row={r} field="base" editable={amountEditable(r)}
                                                                 onOpen={openAmount} />
                                        </td>
                                        <td className="px-3 py-1.5 text-right">
                                            <ReportingAmountCell row={r} field="tax" editable={amountEditable(r)}
                                                                 onOpen={openAmount} />
                                        </td>
                                        <td className="px-3 py-1.5">
                                            <DocumentLinks row={r} company={company} />
                                        </td>
                                        <td className="px-3 py-1.5 no-underline">
                                            <ExclusionActionCell row={r} canEdit={!!canEdit}
                                                                 periodLocked={periodLocked}
                                                                 onAct={openAction} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50 border-t font-medium">
                                <tr>
                                    <td className="px-3 py-2" colSpan={7}>
                                        {activeCount > 0
                                            ? t('drill.filteredCount', { shown: filtered.length, total: rows.length })
                                            : t('drill.count', { count: total })}
                                        {truncated && ` ${t('drill.showingFirst', { count: rows.length })}`}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {money(filtered.reduce((s, r) => s + r.baseLocal, 0))}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {money(filtered.reduce((s, r) => s + r.taxLocal, 0))}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {anyReporting ? money(sumBaseReporting) : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {anyReporting ? money(sumTaxReporting) : '—'}
                                    </td>
                                    <td />
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            </div>

            {dialog && (
                <EntryExclusionDialog
                    action={dialog.action}
                    company={company}
                    year={year}
                    month={month}
                    target={dialog.target}
                    onClose={() => setDialog(null)}
                    onDone={() => {
                        setDialog(null);
                        // The modal reloads its own rows AND tells the page behind it: the
                        // figure that opened this drilldown has just moved, and a panel still
                        // showing the old one would be the drift this page exists to prevent.
                        setReloadKey(k => k + 1);
                        onChanged?.();
                    }}
                />
            )}

            {amountDialog && (
                <AmountOverrideDialog
                    company={company}
                    year={year}
                    month={month}
                    row={amountDialog.row}
                    field={amountDialog.field}
                    editable={amountEditable(amountDialog.row)}
                    onClose={() => setAmountDialog(null)}
                    onDone={() => {
                        setAmountDialog(null);
                        // Same as an exclusion: the figure behind this drilldown just moved.
                        setReloadKey(k => k + 1);
                        onChanged?.();
                    }}
                />
            )}
        </div>,
        document.body
    );
}

function DocumentLinks({ row, company }: { row: VatEntryRow; company: VatCompany }) {
    const { t } = useVatLang();
    // getBcPostedDocumentUrl is the whole gate: it returns null for a company with no reachable
    // web client and for a document the server could not classify. No company is named here.
    const bcUrl = getBcPostedDocumentUrl(company, row.documentNo, row.bcDocumentKind);

    return (
        <span className="inline-flex items-center gap-2" onClick={e => e.stopPropagation()}>
            {row.docuWareUrl ? (
                <a href={row.docuWareUrl} target="_blank" rel="noopener noreferrer"
                   title={row.docuWareUrlVerified
                       ? t('drill.scanTitle', { confidence: row.docuWareMatchConfidence ?? '?' })
                       : t('drill.scanUnverified')}
                   className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
                    <FileText className="h-3.5 w-3.5" />
                    <span className="text-xs">{t('drill.scan')}</span>
                    {!row.docuWareUrlVerified && <span className="text-xs text-amber-600">?</span>}
                </a>
            ) : (
                <span className="text-xs text-gray-400"
                      title={row.kind === 'Verkauf'
                          ? t('drill.salesNotScanned')
                          : t('drill.noScanLinked')}>
                    {t('drill.noScan')}
                </span>
            )}
            {bcUrl && (
                <a href={bcUrl} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                   title={bcLinkHint(row.bcDocumentKind)}>
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span className="text-xs">BC</span>
                </a>
            )}
        </span>
    );
}
